/**
 * MachineMapper. Pure gesture→param brain.
 * Never touches DOM, audio, or canvas.
 * Mutable state limited to crossfade timestamps + edge-detection bookkeeping.
 *
 * Test count: 13 spec-required + 3 regression/Task 4 tests for active-play
 * crossfade and drag traces + 4 helper unit tests = 20 total.
 *
 * Two time parameters in tick():
 *   - idleMs: ms since LAST INTERACTION. Resets to 0 every time the user touches.
 *             Used ONLY by the idle-evolution branch where "how long since the user
 *             stopped touching" is the correct semantic.
 *   - nowMs:  MONOTONIC wall-clock (e.g. performance.now()). Strictly increases.
 *             Used for crossfade `startedAt` snapshots + `crossfadeFor` calls so
 *             interpolation continues to progress while the user is interacting.
 *
 * Mutable private fields (exceptions to the "pure tick" contract, all documented):
 *   _heldIds          - Set<number>: tracks which hold IDs fired a haptic this press
 *                       (edge-triggered: fires haptic once per new hold, not every tick)
 *   _releaseMap       - Map<number, {startedAt: number, prevRate: number}>: tracks
 *                       when each hold released (nowMs) so we can ramp bedRate back over 1.5s
 *   _modeIndex        - number: current mode index 0|1|2 (updated when modeChange emits)
 *   _modeChangedAtNow - number | null: monotonic-clock timestamp of last modeChange emission
 *   _dtCounter        - number: double-tap cycle counter for 6-8 index cycling
 *   _lastSwipeAtNow   - number: monotonic-clock timestamp of last modeChange (debounce)
 *   _idleBoundary     - number: last idle boundary count crossed (for inject edge-detect)
 */

import type {
  GestureFrame,
  ModeConfig,
  MachineMapperOutput,
  AudioParams,
  VisualParams,
  TouchStamp,
} from '../types';

// ─── Config ───────────────────────────────────────────────────────────────────

export interface MachineMapperConfig {
  /** Deterministic seed for idle-evolution oneshot selection. */
  seed: number;
  /** Idle threshold before evolution begins. Spec §7: 8s. */
  idleEvolveMs: number;
  /** Bar period (ms) — idle injection cadence is every 4–8 bars. */
  barMs: number;
}

// ─── Feel-tuning constants ────────────────────────────────────────────────────
// Centralized so a reader can skim the file's "feel" at a glance.

const CROSSFADE_MS = 1500;             // mode change + hold release
const GRIT_COEFFICIENT = 0.3;          // velocity → bedRate (drag)
const HOLD_BED_TARGET = 0.5;           // bedRate lerp target while holding
const HOLD_BED_LERP_T = 0.5;           // how far toward target on each hold tick
const HOLD_FEEDBACK_FREEZE = 0.95;     // visual.feedback floor during hold
const HOLD_BLUR = 0.75;                // held finger softens the video surface
const HOLD_WARP = 0.4;                 // held finger makes the surface malleable
const TAP_BLOOM_BOOST = 0.15;          // audio + visual feedback nudge per tap
const PINCH_SPACE_COEFF = 0.4;         // pinch-out → audio.space
const PINCH_ZOOM_OUT_COEFF = 0.3;      // pinch-out → visual.zoom
const PINCH_FILTER_COEFF = 0.4;        // pinch-in → audio.filter (lower)
const PINCH_ZOOM_IN_COEFF = 0.2;       // pinch-in → visual.zoom (lower)
const PINCH_BLUR_COEFF = 0.9;          // pinch-in → soften/compress
const PINCH_WARP_COEFF = 0.6;          // pinch → elastic field strength
const ROTATE_CHROMA_COEFF = 0.5;       // rotate → audio.chroma
const ROTATE_VISUAL_COEFF = 0.3;       // rotate → visual.rotation
const ROTATE_TWIST_COEFF = 0.8;        // rotate → distortion-field twist
const DRAG_FLOW_COEFF = 2;             // velocity → directional smear
const DRAG_WARP_COEFF = 1.0;           // velocity → local displacement strength
const DRAG_BLUR_COEFF = 0.35;          // velocity → small motion blur
const SWIPE_VX_THRESHOLD = 0.3;        // 2-finger swipe minimum vx
const TOUCHSTAMP_INTENSITY_COEFF = 4;  // velMag → touchStamp.intensity
const DRAG_TRACE_STEP = 0.09;
const DRAG_TRACE_MAX_OFFSET = 0.06;
const DRAG_TRACE_STAMPS = [
  { distance: 1, intensity: 0.5 },
  { distance: 2, intensity: 0.25 },
] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function clampSigned(x: number): number {
  return Math.max(-1, Math.min(1, x));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * clamp01(t);
}

/**
 * Exponential scale: maps t ∈ [0,1] to [min, max] on an exponential curve.
 * Returns min * (max/min)^clamp01(t).
 * Guards against min=0 (log axis is undefined) by falling back to linear lerp.
 * Exported for unit testing.
 */
export function expScale(t: number, [min, max]: [number, number]): number {
  if (min <= 0) return lerp(0, max, clamp01(t)); // log axis requires positive min
  return min * Math.pow(max / min, clamp01(t));
}

/**
 * Project an Hz value back to 0..1 on the log axis defined by [min, max].
 * Inverse of expScale. Used by drag so audio.filter (0..1) reflects the
 * exp-scaled position along the cutoff range without losing the perceptual curve.
 * Guards against min=0 (log axis is undefined) by falling back to linear projection.
 * Exported for unit testing.
 */
export function expScaleInverse(hz: number, [min, max]: [number, number]): number {
  if (min <= 0) return clamp01(hz / max);
  return clamp01(Math.log(hz / min) / Math.log(max / min));
}

/**
 * Crossfade progress in 0..1. clamp01((now - started) / durMs).
 * Used for mode-change interpolation (1.5s) and hold-release ramp-back (1.5s).
 * `now` and `started` MUST come from the same monotonic clock.
 */
function crossfadeFor(now: number, started: number, durMs: number): number {
  return clamp01((now - started) / durMs);
}

/** Parse a key string like "A minor" or "C minor" to root MIDI note (octave 4). */
function parseRootMidi(key: string): number {
  const noteMap: Record<string, number> = {
    C: 60, 'C#': 61, Db: 61,
    D: 62, 'D#': 63, Eb: 63,
    E: 64,
    F: 65, 'F#': 66, Gb: 66,
    G: 67, 'G#': 68, Ab: 68,
    A: 69, 'A#': 70, Bb: 70,
    B: 71,
  };
  const notePart = key.split(' ')[0] ?? 'A';
  return noteMap[notePart] ?? 69;
}

/** Minor pentatonic intervals in semitones: [0, 3, 5, 7, 10] */
const MINOR_PENTA_INTERVALS = [0, 3, 5, 7, 10] as const;

/**
 * Map y ∈ 0..1 to a MIDI note in minor pentatonic within [midiLo, midiHi],
 * anchored to the key's root. Returns a bedRate ratio: 2^((midi - root) / 12).
 */
function quantizeToMinorPentatonic(
  y: number,
  key: string,
  pitchRange: [number, number]
): number {
  const [midiLo, midiHi] = pitchRange;
  const rootMidi = parseRootMidi(key);

  // Build pentatonic notes in range
  const notes: number[] = [];
  // Span enough octaves to cover the range
  for (let oct = -3; oct <= 5; oct++) {
    for (const interval of MINOR_PENTA_INTERVALS) {
      const note = rootMidi + oct * 12 + interval;
      if (note >= midiLo && note <= midiHi) {
        notes.push(note);
      }
    }
  }
  notes.sort((a, b) => a - b);

  if (notes.length === 0) {
    // Fallback: return neutral bedRate
    return 1.0;
  }

  // Map y to an index (y=0 → lowest note, y=1 → highest note)
  const idx = Math.round(clamp01(y) * (notes.length - 1));
  const targetMidi = notes[Math.max(0, Math.min(idx, notes.length - 1))]!;
  // Convert to bedRate: 2^((midi - rootMidi) / 12)
  return Math.pow(2, (targetMidi - rootMidi) / 12);
}

/**
 * Bar period (in bars) between idle injections.
 * Constant per seed — every session picks a different bar period in 4..8.
 */
function boundaryBars(seed: number): number {
  return 4 + (seed % 5); // → 4,5,6,7,8 cyclically based on seed
}

/** Neutral audio params for a given mode. */
function neutralAudio(): AudioParams {
  return {
    bedRate: 1.0,
    feedback: 0.3,
    filter: 0.5,
    chroma: 0.0,
    space: 0.0,
  };
}

/** Neutral visual params. */
function neutralVisual(): VisualParams {
  return {
    feedback: 0.0,
    zoom: 0.5,     // shader maps zoom01=0.5 → no zoom (scale=mix(1.0, 0.94, x)). 0 was a no-op too.
    chroma: 0.0,
    rotation: 0.5, // shader maps rot01=0.5 → no rotation ((x - 0.5) * 0.04 rad). 0 = constant CCW swirl.
    flowX: 0.0,
    flowY: 0.0,
    warp: 0.0,
    blur: 0.0,
    twist: 0.0,
  };
}

// ─── MachineMapper ────────────────────────────────────────────────────────────

export class MachineMapper {
  private readonly _cfg: MachineMapperConfig;

  // Edge-detection mutable state (see file-level JSDoc for field descriptions)
  private _heldIds: Set<number> = new Set();
  private _releaseMap: Map<number, { startedAt: number; prevRate: number }> = new Map();
  private _modeIndex: 0 | 1 | 2 = 0;
  private _modeChangedAtNow: number | null = null;
  private _dtCounter: number;
  private _lastSwipeAtNow: number = -Infinity;
  private _idleBoundary: number = -1;

  constructor(cfg: MachineMapperConfig) {
    this._cfg = cfg;
    this._dtCounter = cfg.seed;
  }

  /**
   * Pure tick. Same inputs (including instance state) → same output.
   *
   * @param gesture  Current frame's gesture state.
   * @param mode     Active mode config (post-modeChange swap).
   * @param idleMs   Ms since last interaction. Resets to 0 on every touch.
   *                 Used only by the idle-evolution branch.
   * @param nowMs    Monotonic wall-clock (e.g. performance.now()). Used for
   *                 crossfade timing so progress continues during active play.
   * @param prev     Previous tick's output (for lerp anchors, edge detection).
   */
  tick(
    gesture: GestureFrame,
    mode: ModeConfig,
    idleMs: number,
    nowMs: number,
    prev: MachineMapperOutput | null
  ): MachineMapperOutput {
    const prevAudio = prev?.audio ?? neutralAudio();
    const prevVisual = prev?.visual ?? neutralVisual();

    // Start with neutral / prev values
    let audio: AudioParams = { ...neutralAudio() };
    let visual: VisualParams = { ...neutralVisual() };
    const touchStamps: TouchStamp[] = [];
    const events: MachineMapperOutput['events'] = [];

    const fingerCount = gesture.fingers.size;

    // ─── Crossfade from prev mode (driven by monotonic nowMs) ────────────────
    if (this._modeChangedAtNow !== null) {
      const t = crossfadeFor(nowMs, this._modeChangedAtNow, CROSSFADE_MS);
      // Lerp from prev audio/visual toward neutral
      const targetAudio = neutralAudio();
      const targetVisual = neutralVisual();
      audio.bedRate = lerp(prevAudio.bedRate, targetAudio.bedRate, t);
      audio.feedback = lerp(prevAudio.feedback, targetAudio.feedback, t);
      audio.filter = lerp(prevAudio.filter, targetAudio.filter, t);
      audio.chroma = lerp(prevAudio.chroma, targetAudio.chroma, t);
      audio.space = lerp(prevAudio.space, targetAudio.space, t);
      visual.feedback = lerp(prevVisual.feedback, targetVisual.feedback, t);
      visual.zoom = lerp(prevVisual.zoom, targetVisual.zoom, t);
      visual.chroma = lerp(prevVisual.chroma, targetVisual.chroma, t);
      visual.rotation = lerp(prevVisual.rotation, targetVisual.rotation, t);
      visual.flowX = lerp(prevVisual.flowX, targetVisual.flowX, t);
      visual.flowY = lerp(prevVisual.flowY, targetVisual.flowY, t);
      visual.warp = lerp(prevVisual.warp, targetVisual.warp, t);
      visual.blur = lerp(prevVisual.blur, targetVisual.blur, t);
      visual.twist = lerp(prevVisual.twist, targetVisual.twist, t);

      if (t >= 1.0) {
        this._modeChangedAtNow = null;
      }
    } else {
      // No active crossfade: start from neutral
      audio = { ...neutralAudio() };
      visual = { ...neutralVisual() };
    }

    // ─── 5-finger easter egg (highest priority — checked first) ───────────────
    if (fingerCount === 5) {
      if ((prevAudio.feedback ?? 0) < 1) {
        audio.feedback = 1;
        visual.feedback = 1;
        events.push({ kind: 'haptic', flavor: 'tap' });
      } else {
        // Auto-duck on subsequent 5-finger tick
        audio.bedRate = (prevAudio.bedRate ?? 1) * 0.5;
        audio.feedback = 0.5;
        visual.feedback = 0.5;
      }
      return { audio, visual, touchStamps, events };
    }

    // ─── 2-finger swipe → mode change (debounce via monotonic nowMs) ─────────
    if (fingerCount === 2) {
      const fingers = Array.from(gesture.fingers.values());
      const [f1, f2] = [fingers[0]!, fingers[1]!];
      const both = Math.abs(f1.vx) > SWIPE_VX_THRESHOLD && Math.abs(f2.vx) > SWIPE_VX_THRESHOLD;
      const sameDir = Math.sign(f1.vx) === Math.sign(f2.vx);
      const lateral = Math.abs(f1.vx) > Math.abs(f1.vy) && Math.abs(f2.vx) > Math.abs(f2.vy);
      const debounced = (nowMs - this._lastSwipeAtNow) > CROSSFADE_MS;

      if (both && sameDir && lateral && debounced) {
        const dir = f1.vx > 0 ? 1 : -1;
        const nextIndex = ((this._modeIndex + dir + 3) % 3) as 0 | 1 | 2;
        this._modeIndex = nextIndex;
        this._modeChangedAtNow = nowMs;
        this._lastSwipeAtNow = nowMs;
        visual.flowX = dir;
        visual.warp = Math.max(visual.warp, 0.45);
        events.push({ kind: 'modeChange', toIndex: nextIndex });
      }
    }

    // ─── Pinch ────────────────────────────────────────────────────────────────
    if (gesture.pinch !== null) {
      const { delta } = gesture.pinch;
      if (delta > 0) {
        // Pinch out → bigger reverb + zoom
        audio.space = clamp01((prevAudio.space ?? 0) + delta * PINCH_SPACE_COEFF);
        visual.zoom = clamp01(prevVisual.zoom + delta * PINCH_ZOOM_OUT_COEFF);
        visual.warp = clamp01(prevVisual.warp + delta * PINCH_WARP_COEFF);
        visual.blur = clamp01(prevVisual.blur - delta * PINCH_BLUR_COEFF * 0.5);
      } else {
        // Pinch in → filter lower + zoom lower
        audio.filter = clamp01((prevAudio.filter ?? 0.5) - Math.abs(delta) * PINCH_FILTER_COEFF);
        visual.zoom = clamp01(prevVisual.zoom - Math.abs(delta) * PINCH_ZOOM_IN_COEFF);
        visual.warp = clamp01(prevVisual.warp + Math.abs(delta) * PINCH_WARP_COEFF);
        visual.blur = clamp01(prevVisual.blur + Math.abs(delta) * PINCH_BLUR_COEFF);
      }
    }

    // ─── Rotate ───────────────────────────────────────────────────────────────
    if (gesture.rotate !== null) {
      const { delta } = gesture.rotate;
      audio.chroma = clamp01((prevAudio.chroma ?? 0) + delta * ROTATE_CHROMA_COEFF);
      visual.rotation = clamp01(prevVisual.rotation + delta * ROTATE_VISUAL_COEFF);
      visual.twist = clampSigned(prevVisual.twist + delta * ROTATE_TWIST_COEFF);
    }

    // ─── Hold (>400ms) ────────────────────────────────────────────────────────
    for (const hold of gesture.holds) {
      if (hold.durationMs >= 400) {
        // Duck bed rate
        audio.bedRate = lerp(prevAudio.bedRate ?? 1, HOLD_BED_TARGET, HOLD_BED_LERP_T);
        // Freeze feedback high
        visual.feedback = Math.max(prevVisual.feedback ?? 0, HOLD_FEEDBACK_FREEZE);
        visual.blur = Math.max(visual.blur, HOLD_BLUR);
        visual.warp = Math.max(visual.warp, HOLD_WARP);

        // Edge-triggered haptic: fire only on first tick of this hold
        if (!this._heldIds.has(hold.id)) {
          this._heldIds.add(hold.id);
          events.push({ kind: 'haptic', flavor: 'tap' });
        }
      }
    }

    // Detect hold release: held IDs no longer in current holds (uses monotonic nowMs)
    const currentHoldIds = new Set(gesture.holds.map(h => h.id));
    for (const heldId of this._heldIds) {
      if (!currentHoldIds.has(heldId)) {
        this._heldIds.delete(heldId);
        // Track release for ramp-back
        this._releaseMap.set(heldId, { startedAt: nowMs, prevRate: audio.bedRate });
      }
    }

    // Handle hold release ramp-back (over 1.5s, driven by monotonic nowMs)
    for (const [releaseId, { startedAt, prevRate }] of this._releaseMap) {
      const t = crossfadeFor(nowMs, startedAt, CROSSFADE_MS);
      audio.bedRate = lerp(prevRate, 1.0, t);
      visual.feedback = lerp(HOLD_FEEDBACK_FREEZE, 0, t);
      visual.blur = Math.max(visual.blur, lerp(HOLD_BLUR, 0, t));
      visual.warp = Math.max(visual.warp, lerp(HOLD_WARP, 0, t));
      if (t >= 1.0) {
        this._releaseMap.delete(releaseId);
      }
    }

    // ─── 1-finger drag ────────────────────────────────────────────────────────
    if (fingerCount === 1 && gesture.holds.length === 0 && gesture.taps.length === 0) {
      const finger = gesture.fingers.values().next().value!;
      // filter: exponential position along the range, projected back to 0..1
      const hz = expScale(finger.x, mode.gestureRanges.dragXFilter);
      audio.filter = expScaleInverse(hz, mode.gestureRanges.dragXFilter);
      // bedRate: pentatonic pitch quantization at y, plus velocity-based grit term
      const velMag = Math.sqrt(finger.vx * finger.vx + finger.vy * finger.vy);
      const pitched = quantizeToMinorPentatonic(finger.y, mode.key, mode.gestureRanges.dragYPitch);
      // Spec §7 row 2: velocity → bedRate grit (additive nudge, capped at 2)
      audio.bedRate = Math.min(2, pitched + clamp01(velMag) * GRIT_COEFFICIENT);
      visual.flowX = clampSigned(finger.vx * DRAG_FLOW_COEFF);
      visual.flowY = clampSigned(finger.vy * DRAG_FLOW_COEFF);
      visual.warp = Math.max(visual.warp, clamp01(velMag * DRAG_WARP_COEFF));
      visual.blur = Math.max(visual.blur, clamp01(velMag * DRAG_BLUR_COEFF));
      const contactIntensity = clamp01(0.75 + velMag * TOUCHSTAMP_INTENSITY_COEFF);
      const traceBaseX = finger.vx * DRAG_TRACE_STEP;
      const traceBaseY = finger.vy * DRAG_TRACE_STEP;
      const traceLen = Math.hypot(traceBaseX, traceBaseY);
      const traceScale = traceLen > DRAG_TRACE_MAX_OFFSET ? DRAG_TRACE_MAX_OFFSET / traceLen : 1;
      const traceDx = traceBaseX * traceScale;
      const traceDy = traceBaseY * traceScale;
      touchStamps.push({
        id: finger.id,
        x: finger.x,
        y: finger.y,
        intensity: contactIntensity,
      });
      for (const trace of DRAG_TRACE_STAMPS) {
        touchStamps.push({
          id: finger.id,
          x: clamp01(finger.x - traceDx * trace.distance),
          y: clamp01(finger.y - traceDy * trace.distance),
          intensity: clamp01(contactIntensity * trace.intensity),
        });
      }
    }

    // ─── Taps ────────────────────────────────────────────────────────────────
    for (const tap of gesture.taps) {
      const injectIndex = this._cfg.seed % 6; // index 0..5 for standard oneshots
      events.push({ kind: 'inject', index: injectIndex, x: tap.x, y: tap.y });
      events.push({ kind: 'haptic', flavor: 'tap' });
      audio.feedback = clamp01((prevAudio.feedback ?? 0.3) + TAP_BLOOM_BOOST);
      visual.feedback = clamp01((prevVisual.feedback ?? 0) + TAP_BLOOM_BOOST);
    }

    // ─── Double-taps ─────────────────────────────────────────────────────────
    for (const dt of gesture.doubleTaps) {
      const index = ((this._cfg.seed + this._dtCounter) % 3) + 6; // → 6, 7, or 8
      this._dtCounter++;
      events.push({ kind: 'inject', index, x: dt.x, y: dt.y });
      events.push({ kind: 'haptic', flavor: 'hard' });
    }

    // ─── Idle evolution (driven by idleMs — "how long since user stopped") ───
    if (idleMs > this._cfg.idleEvolveMs) {
      const bars = boundaryBars(this._cfg.seed);
      const periodMs = bars * this._cfg.barMs;
      const elapsed = idleMs - this._cfg.idleEvolveMs;
      const boundaryCount = Math.floor(elapsed / periodMs);

      if (boundaryCount > this._idleBoundary) {
        this._idleBoundary = boundaryCount;
        const injectIdx = ((this._cfg.seed + boundaryCount) % 5) + 1; // → 1..5
        events.push({ kind: 'inject', index: injectIdx, x: 0.5, y: 0.5 });
      }

      // Keep idle visually clean. Interaction is the only source of video
      // distortion; idle evolution may emit audio events but does not move
      // shader controls.
    }

    return { audio, visual, touchStamps, events };
  }
}
